import React, { useCallback, useEffect, useState } from "react";
import { FaEdit, FaPlus } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import {
  AlertBox,
  formatShortDate,
  LoadingSpinner,
  PrimaryButton,
  SecondaryButton,
  SkeletonRows,
} from "../UI/DesignStandards";
import {
  DEFAULT_MIN_GAMES_FOR_AVERAGE,
  clearTournamentAverageCatalog,
  createAverageCatalog,
  extractLeagueSheetFiles,
  getTournamentAverageCatalog,
  listAverageCatalogs,
  listLeagueSheetImports,
  parseMinGamesForAverage,
  setTournamentAverageCatalog,
} from "../../utility/averageCatalogs";
import {
  FEATURE_AI_LEAGUE_AVERAGE_IMPORT,
  tournamentHasAiFeature,
} from "../../utility/features";
import {
  LeagueSheetUploadFields,
  LeagueSheetUploadForm,
} from "../organizations/AverageCatalogs/LeagueSheetUploadForm";

const NEW_CATALOG = "__new__";

const sheetStatusLabel = (row) => {
  if (row.status === "uploaded") return "Uploaded";
  if (row.status === "processing") return "Processing";
  if (row.status === "completed") return "Completed";
  if (row.status === "failed") return "Failed";
  return row.status;
};

const TournamentAverageCatalogCard = ({ tournament, onSaved }) => {
  const { user } = useAuth();
  const [allowed, setAllowed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [catalogs, setCatalogs] = useState([]);
  const [imports, setImports] = useState([]);
  const [catalogID, setCatalogID] = useState("");
  const [newName, setNewName] = useState("");
  const [newAsOfDate, setNewAsOfDate] = useState("");
  const [minGames, setMinGames] = useState(
    String(DEFAULT_MIN_GAMES_FOR_AVERAGE)
  );
  const [files, setFiles] = useState([]);
  const [leagueName, setLeagueName] = useState("");
  const [savedCatalogID, setSavedCatalogID] = useState("");
  const [savedMinGames, setSavedMinGames] = useState(
    String(DEFAULT_MIN_GAMES_FOR_AVERAGE)
  );
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractProgress, setExtractProgress] = useState(null);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");

  const organizationID = tournament?.organizationID;
  const isNewCatalog = catalogID === NEW_CATALOG;
  const selectedCatalogID = isNewCatalog ? "" : catalogID;
  const savedCatalog = catalogs.find((row) => row.id === savedCatalogID);

  const loadImports = useCallback(
    async (nextCatalogID) => {
      if (!organizationID || !nextCatalogID) {
        setImports([]);
        return;
      }
      try {
        setImports(await listLeagueSheetImports(nextCatalogID, organizationID));
      } catch (err) {
        console.error(err);
        setError("Could not load league sheets for this catalog.");
      }
    },
    [organizationID]
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!organizationID) {
        setLoading(false);
        return;
      }
      try {
        const enabled = await tournamentHasAiFeature(
          organizationID,
          FEATURE_AI_LEAGUE_AVERAGE_IMPORT,
          tournament.id
        );
        if (!enabled) {
          if (!cancelled) setAllowed(false);
          return;
        }
        const [rows, link] = await Promise.all([
          listAverageCatalogs(organizationID),
          getTournamentAverageCatalog(tournament.id),
        ]);
        if (cancelled) return;
        const nextCatalogID = link?.catalogID || "";
        const nextMinGames = String(
          link?.minGamesForAverage || DEFAULT_MIN_GAMES_FOR_AVERAGE
        );
        setAllowed(true);
        setCatalogs(rows);
        setCatalogID(nextCatalogID);
        setSavedCatalogID(nextCatalogID);
        setMinGames(nextMinGames);
        setSavedMinGames(nextMinGames);
        if (nextCatalogID) {
          setImports(await listLeagueSheetImports(nextCatalogID, organizationID));
        } else {
          setImports([]);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setError("Could not load average catalogs.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [tournament?.id, organizationID]);

  const resetDraft = () => {
    setCatalogID(savedCatalogID);
    setMinGames(savedMinGames);
    setNewName("");
    setNewAsOfDate("");
    setFiles([]);
    setLeagueName("");
    setShowUpload(false);
    setError("");
    setWarning("");
  };

  const handleEditClick = () => {
    resetDraft();
    if (!savedCatalogID && catalogs.length === 0) {
      setCatalogID(NEW_CATALOG);
    }
    setIsEditing(true);
  };

  const handleCancel = () => {
    resetDraft();
    setIsEditing(false);
  };

  const handleCatalogChange = (value) => {
    setCatalogID(value);
    if (value !== NEW_CATALOG) {
      setNewName("");
      setNewAsOfDate("");
    }
  };

  const handleFileReject = (message) => {
    setError(message || "");
    setWarning("");
  };

  const runExtract = async (nextCatalogID) => {
    if (!files.length || !nextCatalogID || !organizationID) return;
    setExtracting(true);
    setExtractProgress({ done: 0, total: files.length });
    try {
      const result = await extractLeagueSheetFiles({
        catalogID: nextCatalogID,
        organizationID,
        leagueName,
        files,
        createdBy: user?.id,
        tournamentID: tournament.id,
        onProgress: setExtractProgress,
        onFileDone: () => loadImports(nextCatalogID),
      });
      setFiles([]);
      setLeagueName("");
      setShowUpload(false);
      await loadImports(nextCatalogID);
      if (result.emptySheets.length) {
        setWarning(result.emptySheets.join(" "));
      }
      if (result.failures.length) {
        setError(result.failures.join(" "));
      }
    } finally {
      setExtracting(false);
      setExtractProgress(null);
    }
  };

  const handleSave = async () => {
    if (isNewCatalog && !newName.trim()) {
      setError("Enter a catalog name.");
      return;
    }
    const parsed = parseMinGamesForAverage(minGames);
    if ((isNewCatalog || catalogID) && !parsed) {
      setError(
        "Minimum games for average must be a whole number greater than 0."
      );
      return;
    }
    setSaving(true);
    setError("");
    setWarning("");
    try {
      let nextCatalogID = selectedCatalogID;
      if (isNewCatalog) {
        const catalog = await createAverageCatalog({
          organizationID,
          name: newName.trim(),
          asOfDate: newAsOfDate,
          createdBy: user?.id,
        });
        nextCatalogID = catalog.id;
        setCatalogs((current) => [catalog, ...current]);
      }
      if (nextCatalogID) {
        await setTournamentAverageCatalog({
          tournamentID: tournament.id,
          catalogID: nextCatalogID,
          minGamesForAverage: parsed,
          linkedBy: user?.id,
        });
        setSavedMinGames(String(parsed));
        setMinGames(String(parsed));
      } else {
        await clearTournamentAverageCatalog(tournament.id);
        setSavedMinGames(String(DEFAULT_MIN_GAMES_FOR_AVERAGE));
        setMinGames(String(DEFAULT_MIN_GAMES_FOR_AVERAGE));
      }
      setSavedCatalogID(nextCatalogID);
      setCatalogID(nextCatalogID);
      setNewName("");
      setNewAsOfDate("");
      if (onSaved) onSaved();
      if (files.length && nextCatalogID) {
        await runExtract(nextCatalogID);
      } else {
        setFiles([]);
        setLeagueName("");
        await loadImports(nextCatalogID);
      }
      setIsEditing(false);
      setShowUpload(false);
    } catch (err) {
      console.error(err);
      setError(err.message || "Could not save the average catalog.");
    } finally {
      setSaving(false);
    }
  };

  const handleUploadOnly = async (event) => {
    event.preventDefault();
    if (!savedCatalogID) return;
    setError("");
    setWarning("");
    await runExtract(savedCatalogID);
  };

  if (!allowed && !loading) return null;

  if (loading) {
    return (
      <div className="card-board p-6 mb-6">
        <h2 className="heading-display text-xl mb-6">Average Catalog</h2>
        <SkeletonRows count={2} />
      </div>
    );
  }

  const fieldLocked = !isEditing;
  const busy = saving || extracting;
  const labelClass = `text-sm font-medium ${
    fieldLocked ? "text-brand-muted" : "text-brand-charcoal"
  }`;
  const inputClass = `w-full px-3 py-1 border rounded-md text-sm ${
    fieldLocked
      ? "bg-brand-cream text-brand-muted cursor-not-allowed"
      : "bg-white border-brand-charcoal/20 focus:ring-2 focus:ring-brand-gold-ink"
  }`;
  const showUploadFields = isEditing && (isNewCatalog || catalogID);
  const saveLabel =
    files.length && (isNewCatalog || catalogID)
      ? extracting
        ? `Extracting ${extractProgress?.done || 0} of ${extractProgress?.total || files.length}`
        : "Save and extract"
      : saving
      ? "Saving..."
      : "Save Changes";

  return (
    <div className="card-board p-6 mb-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="heading-display text-xl">Average Catalog</h2>
        {!isEditing ? (
          <SecondaryButton
            onClick={handleEditClick}
            icon={FaEdit}
            className="!px-3 !py-2 text-sm"
            disabled={busy}
          >
            Edit Settings
          </SecondaryButton>
        ) : null}
      </div>

      {error ? (
        <AlertBox type="error" title="Error" message={error} />
      ) : null}
      {warning ? (
        <AlertBox
          type="warning"
          title="No bowler averages found"
          message={warning}
        />
      ) : null}

      {extracting && !showUpload ? (
        <div
          className="flex items-center gap-3 mb-4"
          role="status"
          aria-live="polite"
        >
          <LoadingSpinner size="lg" />
          <p className="text-sm text-brand-charcoal">
            {extractProgress
              ? `Extracting ${extractProgress.done} of ${extractProgress.total}. This can take a minute.`
              : "Extracting this league sheet. This can take a minute."}
          </p>
        </div>
      ) : null}

      <div className="card-inset p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className={labelClass}>Catalog</label>
            {fieldLocked ? (
              <p className="text-sm text-brand-charcoal py-1">
                {savedCatalog
                  ? `${savedCatalog.name}${
                      savedCatalog.asOfDate
                        ? ` (as of ${formatShortDate(savedCatalog.asOfDate)})`
                        : ""
                    }`
                  : "None"}
              </p>
            ) : (
              <select
                value={catalogID}
                onChange={(event) => handleCatalogChange(event.target.value)}
                disabled={busy}
                className={inputClass}
              >
                <option value="">None</option>
                <option value={NEW_CATALOG}>New catalog</option>
                {catalogs.map((catalog) => (
                  <option key={catalog.id} value={catalog.id}>
                    {catalog.name}
                    {catalog.asOfDate
                      ? ` (as of ${formatShortDate(catalog.asOfDate)})`
                      : ""}
                  </option>
                ))}
              </select>
            )}
            <p className="text-xs text-brand-muted">
              {fieldLocked
                ? savedCatalogID
                  ? "Averages uses this catalog to fill and verify tournament averages."
                  : "Edit Settings to choose a catalog or create a new one."
                : isNewCatalog
                ? "Creates a catalog for this organization and assigns it to this tournament."
                : "Averages uses this catalog to fill and verify tournament averages."}
            </p>
          </div>
          <div className="space-y-1">
            <label className={labelClass}>Minimum games for average</label>
            {fieldLocked ? (
              <p className="text-sm text-brand-charcoal py-1">
                {savedCatalogID ? savedMinGames : "—"}
              </p>
            ) : (
              <input
                type="number"
                min="1"
                max="400"
                value={minGames}
                onChange={(event) => setMinGames(event.target.value)}
                disabled={busy || (!isNewCatalog && !catalogID)}
                className={inputClass}
              />
            )}
            <p className="text-xs text-brand-muted">
              The catalog average that counts for this tournament is the
              highest with at least this many games.
            </p>
          </div>
        </div>

        {isEditing && isNewCatalog ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-brand-charcoal">
                Name
              </label>
              <input
                type="text"
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                placeholder="January 1, 2026 averages"
                disabled={busy}
                className={inputClass}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-brand-charcoal">
                As of date
              </label>
              <input
                type="date"
                value={newAsOfDate}
                onChange={(event) => setNewAsOfDate(event.target.value)}
                disabled={busy}
                className={inputClass}
              />
              <p className="text-xs text-brand-muted">
                Optional. Use a new catalog when the cutoff date changes.
              </p>
            </div>
          </div>
        ) : null}

        {showUploadFields ? (
          <div className="mt-4 pt-4 border-t border-brand-charcoal/10">
            <p className="text-sm font-medium text-brand-charcoal mb-2">
              League sheets
            </p>
            {extracting ? (
              <div
                className="flex items-center gap-3 mb-3"
                role="status"
                aria-live="polite"
              >
                <LoadingSpinner size="lg" />
                <p className="text-sm text-brand-charcoal">
                  {extractProgress
                    ? `Extracting ${extractProgress.done} of ${extractProgress.total}. This can take a minute.`
                    : "Extracting this league sheet. This can take a minute."}
                </p>
              </div>
            ) : null}
            <LeagueSheetUploadFields
              leagueName={leagueName}
              onLeagueNameChange={setLeagueName}
              onFilesChange={setFiles}
              onReject={handleFileReject}
              disabled={busy}
            />
            {files.length ? (
              <p className="text-xs text-brand-muted mt-2">
                {files.length === 1
                  ? "1 file will extract after Save."
                  : `${files.length} files will extract after Save.`}
              </p>
            ) : (
              <p className="text-xs text-brand-muted mt-2">
                Optional. You can also upload after you save.
              </p>
            )}
          </div>
        ) : null}
      </div>

      {!isEditing && savedCatalogID ? (
        <div className="mt-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <p className="text-sm font-medium text-brand-charcoal">
              {imports.length === 1
                ? "1 league sheet"
                : `${imports.length} league sheets`}
            </p>
            {!showUpload ? (
              <SecondaryButton
                type="button"
                icon={FaPlus}
                className="!px-3 !py-2 text-sm"
                onClick={() => {
                  setError("");
                  setWarning("");
                  setShowUpload(true);
                }}
                disabled={busy}
              >
                Upload league sheets
              </SecondaryButton>
            ) : null}
          </div>
          {showUpload ? (
            <div className="card-inset p-4 mb-3">
              <LeagueSheetUploadForm
                files={files}
                leagueName={leagueName}
                onFilesChange={setFiles}
                onLeagueNameChange={setLeagueName}
                onReject={handleFileReject}
                onSubmit={handleUploadOnly}
                onCancel={() => {
                  setFiles([]);
                  setLeagueName("");
                  setShowUpload(false);
                }}
                extracting={extracting}
                extractProgress={extractProgress}
                showCancel
              />
            </div>
          ) : null}
          {imports.length ? (
            <ul className="text-sm text-brand-charcoal divide-y divide-brand-charcoal/10 border border-brand-charcoal/10 rounded-lg overflow-hidden">
              {imports.map((row) => (
                <li
                  key={row.id}
                  className="px-3 py-2 flex items-start justify-between gap-3"
                >
                  <span className="min-w-0">
                    <span className="block truncate">
                      {row.leagueName || row.fileName}
                    </span>
                    {row.leagueName && row.fileName ? (
                      <span className="block text-xs text-brand-muted truncate">
                        {row.fileName}
                      </span>
                    ) : null}
                  </span>
                  <span className="text-xs text-brand-muted shrink-0">
                    {sheetStatusLabel(row)}
                  </span>
                </li>
              ))}
            </ul>
          ) : !showUpload ? (
            <p className="text-sm text-brand-muted">
              No league sheets in this catalog yet.
            </p>
          ) : null}
        </div>
      ) : null}

      {isEditing ? (
        <div className="flex space-x-4 pt-4">
          <PrimaryButton onClick={handleSave} disabled={busy} loading={busy}>
            {saveLabel}
          </PrimaryButton>
          <SecondaryButton onClick={handleCancel} disabled={busy}>
            Cancel
          </SecondaryButton>
        </div>
      ) : null}
    </div>
  );
};

export default TournamentAverageCatalogCard;
